"use client";
import Image from "next/image";
import Container from "../Common/Container";
import classNames from "classnames";
import { memo } from "react";
import AnimatedReveal from "@/Components/HOC/AnimatedReveal";
import { useLoading } from "@/context/loading";

const MobileSectionContent = memo(({ title, sub_title, description, isInitialLoad }) => (
  <div className="px-6 pt-5 lg:max-w-[620px] flex-1 w-full lg:pt-[90px] lg:pb-[124px] flex flex-col gap-[27px] md:gap-[60px] text-center">
    <AnimatedReveal animation="flip-up" delay={isInitialLoad ? 1.8 : 0}>
      <h2 className="text-[25px] leading-[50px] lg:text-[35px] lg:leading-[35px] font-bold font-urbanist mobile_section_heading">
        {title}
      </h2>
    </AnimatedReveal>
    <AnimatedReveal animation="flip-up" delay={isInitialLoad ? 1.9 : 0.1}>
      <p className="text-[20px] leading-[30px] lg:text-[60px] lg:leading-[70px] font-bold">
        {sub_title}
      </p>
    </AnimatedReveal>
    <AnimatedReveal animation="flip-up" delay={isInitialLoad ? 2.0 : 0.2}>
      <p className="text-[16px] leading-[22px] mb-6 text-white lg:text-[30px] lg:leading-[45px] font-urbanist font-normal">
        {description}
      </p>
    </AnimatedReveal>
  </div>
));

MobileSectionContent.displayName = "MobileSectionContent";

const MobileImage = memo(({ image, title, order, isInitialLoad }) => (
  <AnimatedReveal 
    animation="slide-up" 
    delay={isInitialLoad ? 1.7 : 0}
    className={classNames(
      "max-w-[400px] mx-auto px-10 lg:px-0 flex-1 lg:w-full mobile_image_wrapper",
      `fixed_order_${order}`
    )}
  >
    <Image
      src={image}
      alt={title || "Mobile section image"}
      width={800}
      height={600}
      className="w-full h-full object-cover rounded-xl mobile-image hover:opacity-95 transition-opacity"
      priority={order === "0"}
      placeholder="blur"
      blurDataURL="/placeholder.jpg"
      sizes="(max-width: 768px) 100vw, 800px"
      loading={order === "0" ? "eager" : "lazy"}
    />
  </AnimatedReveal>
));

MobileImage.displayName = "MobileImage";

export default function MobileSection({ data }) {
  const { order, pb, image, title, sub_title, description } = data;
  const { isInitialLoad } = useLoading();

  return (
    <Container>
      <section
        id="mobile"
        className={classNames("lg:px-[120px]", {
          "lg:py-[40px]": pb,
        })}
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-center lg:gap-[150px] items-stretch">
          <MobileImage 
            image={image} 
            title={title} 
            order={order} 
            isInitialLoad={isInitialLoad}
          />
          <MobileSectionContent
            title={title}
            sub_title={sub_title}
            description={description}
            isInitialLoad={isInitialLoad}
          />
        </div>
      </section>
    </Container>
  );
}
